import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft,
  LogOut,
  Home,
  Mail,
  Phone,
  Calendar,
  Clock,
  User,
  Activity,
  MessageSquare,
  Plus,
  CheckCircle,
  XCircle,
  UserPlus,
  Loader2,
  Sparkles,
  Ban
} from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showAddFollowUp, setShowAddFollowUp] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [newActivity, setNewActivity] = useState({ activity_type: 'note', details: '' });
  const [newFollowUp, setNewFollowUp] = useState({ follow_up_type: 'email', scheduled_date: '', notes: '', priority: 'normal' });
  const [convertData, setConvertData] = useState({ service_type: '', notes: '' });
  const [scoringBusy, setScoringBusy] = useState(false);
  const [blocking, setBlocking] = useState(false);

  const handleBlock = async () => {
    if (!window.confirm(`Block ${lead.email}${lead.phone ? ' and ' + lead.phone : ''}? Future submissions from them will be auto-flagged as spam.`)) return;
    setBlocking(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/leads/${leadId}/block`, {}, { withCredentials: true });
      toast.success(`Blocked. ${res.data.flagged} lead(s) moved to Spam.`);
      navigate('/admin/leads');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block');
    } finally {
      setBlocking(false);
    }
  };

  const runAiScore = async () => {
    setScoringBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/leads/${leadId}/score`, {}, { withCredentials: true });
      setLead((l) => ({ ...l, ...res.data }));
      toast.success(`AI score: ${res.data.ai_score} (${res.data.ai_tier})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI scoring failed');
    } finally {
      setScoringBusy(false);
    }
  };

  const tierColor = (tier) => ({
    'red-hot': 'bg-red-100 text-red-700 border-red-200',
    hot: 'bg-orange-100 text-orange-700 border-orange-200',
    warm: 'bg-amber-100 text-amber-700 border-amber-200',
    cold: 'bg-slate-100 text-slate-700 border-slate-200',
  }[tier] || 'bg-slate-100 text-slate-700 border-slate-200');

  useEffect(() => {
    fetchLeadData();
  }, [leadId]);

  const fetchLeadData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/leads/${leadId}`, { withCredentials: true });
      setLead(response.data.lead);
      setActivities(response.data.activities);
    } catch (error) {
      console.error('Error fetching lead:', error);
      toast.error('Failed to load lead data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/admin/leads/${leadId}`, { status: newStatus }, { withCredentials: true });
      setLead({ ...lead, status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchLeadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/admin/leads/${leadId}/activity`,
        { lead_id: leadId, ...newActivity },
        { withCredentials: true }
      );
      toast.success('Activity added');
      setShowAddActivity(false);
      setNewActivity({ activity_type: 'note', details: '' });
      fetchLeadData();
    } catch (error) {
      toast.error('Failed to add activity');
    }
  };

  const handleAddFollowUp = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/admin/follow-ups`,
        { lead_id: leadId, ...newFollowUp },
        { withCredentials: true }
      );
      toast.success('Follow-up scheduled');
      setShowAddFollowUp(false);
      setNewFollowUp({ follow_up_type: 'email', scheduled_date: '', notes: '', priority: 'normal' });
      fetchLeadData();
    } catch (error) {
      toast.error('Failed to schedule follow-up');
    }
  };

  const handleConvertToClient = async (e) => {
    e.preventDefault();
    if (!convertData.service_type) {
      toast.error('Please select a service type');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/admin/clients/convert`,
        { lead_id: leadId, ...convertData },
        { withCredentials: true }
      );
      toast.success('Lead converted to client!');
      setShowConvert(false);
      fetchLeadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-[#FFF7ED] text-[#F97316]',
      'contacted': 'bg-[#EFF6FF] text-[#1D4ED8]',
      'qualified': 'bg-green-100 text-green-700',
      'converted': 'bg-purple-100 text-purple-700',
      'closed': 'bg-[#F1F5F9] text-[#475569]'
    };
    return colors[status] || 'bg-[#F1F5F9] text-[#475569]';
  };

  const getActivityIcon = (type) => {
    const icons = {
      'lead_created': User,
      'contact_form': MessageSquare,
      'resource_download': Activity,
      'booking_form': Calendar,
      'follow_up_scheduled': Clock,
      'follow_up_updated': CheckCircle,
      'converted_to_client': UserPlus,
      'note': MessageSquare
    };
    return icons[type] || Activity;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-[#0A0F1C] mb-4">Lead not found</h1>
          <Link to="/admin/leads" className="text-[#1D4ED8] hover:underline">Back to Leads</Link>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout title="Lead Detail"><div>
        <Link to="/admin/leads" className="inline-flex items-center gap-2 text-[#475569] hover:text-[#1D4ED8] mb-6">
          <ArrowLeft size={18} />
          Back to Leads
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Lead Info */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg mb-6">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold text-[#0A0F1C]" data-testid="lead-name">{lead.name}</h1>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(lead.status)}`}>
                  {lead.status}
                </span>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <Mail size={16} className="text-[#94A3B8]" />
                  <span className="text-[#475569]">{lead.email}</span>
                </div>
                {lead.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone size={16} className="text-[#94A3B8]" />
                    <span className="text-[#475569]">{lead.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-sm">
                  <Calendar size={16} className="text-[#94A3B8]" />
                  <span className="text-[#475569]">
                    Created: {new Date(lead.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {lead.service_interest && (
                <div className="mb-4">
                  <span className="text-xs text-[#94A3B8] uppercase tracking-wider">Service Interest</span>
                  <p className="text-sm text-[#0A0F1C] font-medium">{lead.service_interest}</p>
                </div>
              )}

              {lead.resource_interest && (
                <div className="mb-4">
                  <span className="text-xs text-[#94A3B8] uppercase tracking-wider">Resource Interest</span>
                  <p className="text-sm text-[#0A0F1C] font-medium">{lead.resource_interest}</p>
                </div>
              )}

              {lead.message && (
                <div className="mb-4">
                  <span className="text-xs text-[#94A3B8] uppercase tracking-wider">Message</span>
                  <p className="text-sm text-[#475569] mt-1 p-3 bg-[#F9FAFB] rounded">{lead.message}</p>
                </div>
              )}

              {/* AI Score panel */}
              <div className="border-t border-[#E2E8F0] pt-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-[#94A3B8] uppercase tracking-wider">AI Lead Score</span>
                  <Button
                    type="button"
                    onClick={runAiScore}
                    disabled={scoringBusy}
                    className="btn-outline text-xs flex items-center gap-1 px-2 py-1"
                    data-testid="ai-score-btn"
                  >
                    {scoringBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {lead.ai_score ? 'Re-score' : 'Score with AI'}
                  </Button>
                </div>
                {lead.ai_score ? (
                  <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3 space-y-2" data-testid="ai-score-panel">
                    <div className="flex items-center gap-2">
                      <div className="text-2xl font-extrabold text-[#0A0F1C]">{lead.ai_score}</div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${tierColor(lead.ai_tier)}`}>{lead.ai_tier}</span>
                    </div>
                    {lead.ai_summary && <p className="text-xs text-[#475569] leading-relaxed">{lead.ai_summary}</p>}
                    {lead.ai_next_action && (
                      <p className="text-xs text-[#0A0F1C]"><strong>Next action:</strong> {lead.ai_next_action}</p>
                    )}
                    {lead.ai_suggested_program && lead.ai_suggested_program !== 'none' && (
                      <p className="text-xs text-[#1D4ED8]"><strong>Suggested program:</strong> {lead.ai_suggested_program}</p>
                    )}
                    {lead.ai_scored_at && (
                      <p className="text-[10px] text-[#94A3B8]">Scored {new Date(lead.ai_scored_at).toLocaleString()}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-[#94A3B8] italic">No AI score yet. Click "Score with AI" to generate one using Claude 4.6.</p>
                )}
              </div>

              {/* Status Actions */}
              <div className="border-t border-[#E2E8F0] pt-4">
                <span className="text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">Update Status</span>
                <div className="flex flex-wrap gap-2">
                  {['new', 'contacted', 'qualified', 'closed'].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                        lead.status === status
                          ? 'bg-[#1D4ED8] text-white'
                          : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                      }`}
                      data-testid={`status-btn-${status}`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => setShowAddFollowUp(true)}
                className="w-full btn-outline flex items-center justify-center gap-2"
                data-testid="schedule-follow-up-btn"
              >
                <Clock size={16} />
                Schedule Follow-Up
              </button>
              <button
                onClick={() => setShowAddActivity(true)}
                className="w-full btn-outline flex items-center justify-center gap-2"
                data-testid="add-note-btn"
              >
                <Plus size={16} />
                Add Note
              </button>
              {lead.status !== 'converted' && (
                <button
                  onClick={() => setShowConvert(true)}
                  className="w-full btn-primary-orange flex items-center justify-center gap-2"
                  data-testid="convert-to-client-btn"
                >
                  <UserPlus size={16} />
                  Convert to Client
                </button>
              )}
              <button
                onClick={handleBlock}
                disabled={blocking}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-600 font-semibold hover:bg-red-50 disabled:opacity-60"
                data-testid="block-lead-btn"
              >
                {blocking ? <Loader2 size={16} className="animate-spin" /> : <Ban size={16} />}
                Block &amp; mark spam
              </button>
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
              <h2 className="text-lg font-bold text-[#0A0F1C] mb-6">Activity Timeline</h2>
              
              {activities.length > 0 ? (
                <div className="space-y-4">
                  {activities.map((activity, index) => {
                    const Icon = getActivityIcon(activity.activity_type);
                    return (
                      <div 
                        key={activity.activity_id || index}
                        className="flex gap-4 pb-4 border-b border-[#F1F5F9] last:border-0"
                        data-testid={`activity-${index}`}
                      >
                        <div className="w-10 h-10 bg-[#EFF6FF] rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon size={18} className="text-[#1D4ED8]" />
                        </div>
                        <div className="flex-grow">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-[#0A0F1C] text-sm capitalize">
                              {activity.activity_type.replace(/_/g, ' ')}
                            </span>
                            <span className="text-xs text-[#94A3B8]">
                              {new Date(activity.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-[#475569]">{activity.details}</p>
                          {activity.source && (
                            <span className="text-xs text-[#94A3B8]">Source: {activity.source}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[#475569] text-center py-8">No activities yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Add Activity Modal */}
        {showAddActivity && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Add Note</h3>
              <form onSubmit={handleAddActivity} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Type</label>
                  <select
                    value={newActivity.activity_type}
                    onChange={(e) => setNewActivity({ ...newActivity, activity_type: e.target.value })}
                    className="form-input w-full"
                  >
                    <option value="note">Note</option>
                    <option value="call">Call</option>
                    <option value="email_sent">Email Sent</option>
                    <option value="meeting">Meeting</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Details</label>
                  <Textarea
                    value={newActivity.details}
                    onChange={(e) => setNewActivity({ ...newActivity, details: e.target.value })}
                    placeholder="Add details..."
                    className="form-input min-h-[100px]"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="button" onClick={() => setShowAddActivity(false)} className="btn-outline flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary-blue flex-1">
                    Add Note
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Follow-Up Modal */}
        {showAddFollowUp && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Schedule Follow-Up</h3>
              <form onSubmit={handleAddFollowUp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Type</label>
                  <select
                    value={newFollowUp.follow_up_type}
                    onChange={(e) => setNewFollowUp({ ...newFollowUp, follow_up_type: e.target.value })}
                    className="form-input w-full"
                  >
                    <option value="email">Email</option>
                    <option value="call">Phone Call</option>
                    <option value="meeting">Meeting</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Scheduled Date</label>
                  <Input
                    type="date"
                    value={newFollowUp.scheduled_date}
                    onChange={(e) => setNewFollowUp({ ...newFollowUp, scheduled_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Priority</label>
                  <select
                    value={newFollowUp.priority}
                    onChange={(e) => setNewFollowUp({ ...newFollowUp, priority: e.target.value })}
                    className="form-input w-full"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Notes</label>
                  <Textarea
                    value={newFollowUp.notes}
                    onChange={(e) => setNewFollowUp({ ...newFollowUp, notes: e.target.value })}
                    placeholder="Add notes..."
                    className="form-input min-h-[80px]"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="button" onClick={() => setShowAddFollowUp(false)} className="btn-outline flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary-blue flex-1">
                    Schedule
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Convert to Client Modal */}
        {showConvert && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Convert to Client</h3>
              <form onSubmit={handleConvertToClient} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Service Type</label>
                  <select
                    value={convertData.service_type}
                    onChange={(e) => setConvertData({ ...convertData, service_type: e.target.value })}
                    className="form-input w-full"
                    required
                  >
                    <option value="">Select a service</option>
                    <option value="Business Jumpstart Strategy">Business Jumpstart Strategy</option>
                    <option value="Business Credibility Setup">Business Credibility Setup</option>
                    <option value="Brand Foundation & Presence">Brand Foundation & Presence</option>
                    <option value="One-on-One Consulting">One-on-One Consulting</option>
                    <option value="Business Systems & Organization">Business Systems & Organization</option>
                    <option value="Training & Workshops">Training & Workshops</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Notes</label>
                  <Textarea
                    value={convertData.notes}
                    onChange={(e) => setConvertData({ ...convertData, notes: e.target.value })}
                    placeholder="Add conversion notes..."
                    className="form-input min-h-[80px]"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="button" onClick={() => setShowConvert(false)} className="btn-outline flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary-orange flex-1">
                    Convert
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div></AdminLayout>);}
